import { useEffect, useState, useRef, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../store/auth/hooks.js";
import "../AppPage.css";
import AudioEditor from "../../components/editor/AudioEditor.js";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../components/ContentTitleBar.js";
import { ClipTitleBarMenu } from "../../components/titleBarMenu/ClipTitleBarMenu.js";
import { canEditClip } from "../../store/AclUtil.js";
import { loadClip, loadChannels } from "./utils/dataLoader.js";
import { useEditor } from "../../store/editor/hooks.js";
import { useChannels } from "../../store/channels/hooks.js";
import { useAnnotations } from "../../store/annotations/hooks.js";
import {
  checkStatusWhitelist,
  getNavigationUrl,
} from "../../store/navigationUtil.js";
import WaveSurfer from "../../components/editor/wavesurfer/wavesurfer.ts";
import Minimap from "../../components/editor/wavesurfer/plugins/minimap.ts";
import Spectrogram from "../../components/editor/wavesurfer/plugins/spectrogram.ts";
import RegionsPlugin from "../../components/editor/wavesurfer/plugins/regions.ts";
import TimelinePlugin from "../../components/editor/wavesurfer/plugins/timeline.ts";
import { HistoryDrawer } from "../../components/HistoryDrawer.js";

export default function EditorPage({ mode = "editor" }) {
  const navigate = useNavigate();
  const { caseId, clipId } = useParams();
  const {
    loadPreferences,
    updatePreferences,
    savePreferences,
    reset: editorReset,
  } = useEditor();
  const { setChannelList } = useChannels();
  const { loadAnnotations, loadSNGAnnotations } = useAnnotations();
  const { user } = useAuth();
  const [clip, setClip] = useState({});
  const [channels, setChannels] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [wavesurfer, setWavesurfer] = useState(null);
  const [regionsPlugin, setRegionsPlugin] = useState(null);
  const containerRef = useRef();
  const minimapRef = useRef();

  const load = useCallback(async () => {
    const currentClip = await loadClip(caseId, clipId);
    if (!currentClip) {
      navigate(getNavigationUrl(caseId));
      return;
    }
    if (!checkStatusWhitelist(currentClip, ["EDITING", "NOISE_FILTERING"])) {
      navigate(getNavigationUrl(null, currentClip));
    }
    editorReset();
    const [preferencesResult, channelList] = await Promise.all([
      loadPreferences(caseId, clipId),
      loadChannels(caseId, clipId),
    ]);
    setChannelList(channelList);

    if (channelList?.length) {
      const loadedChannel = preferencesResult.payload?.selectedChannel ?? 1;
      const isValidChannel =
        channelList[loadedChannel - 1]?.status === "ACTIVE";

      if (!isValidChannel) {
        const activeIndex = channelList.findIndex(
          (channel) => channel.status === "ACTIVE",
        );
        const fallbackChannel = activeIndex >= 0 ? activeIndex + 1 : 1;
        updatePreferences({ selectedChannel: fallbackChannel });
        savePreferences(caseId, clipId, { selectedChannel: fallbackChannel });
      }
    }

    setClip(currentClip);
    setChannels(currentClip.audioFiles);
    // Load annotations if in editor mode
    if (mode === "editor") {
      await loadAnnotations(caseId, clipId);
    }
    if (mode === "sng") {
      await loadSNGAnnotations();
    }
    setLoaded(true);
  }, [
    caseId,
    clipId,
    editorReset,
    loadAnnotations,
    loadSNGAnnotations,
    mode,
    navigate,
    loadPreferences,
    savePreferences,
    setChannelList,
    updatePreferences,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    console.log(containerRef);
    if (containerRef.current) {
      console.log("makin the wavesurfs");
      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: "rgb(31, 78, 179, 1)",
        progressColor: "rgb(31, 78, 179, 1)",
        cursorColor: "rgb(255, 0, 0)",
        height: 500,
      });
      setWavesurfer(ws);

      if (minimapRef.current) {
        const miniMapPlugin = Minimap.create({
          container: minimapRef.current,
          height: 46,
          waveColor: "#808080",
          progressColor: "#222",
          cursorColor: "red",
        });
        ws.registerPlugin(miniMapPlugin);
      }

      const spectrogramPlugin = Spectrogram.create({
        height: 500,
        fftSamples: 512, // fftSamples,
        frequencyMin: 0, // fftFrequencies.min,
        frequencyMax: 20000, // fftFrequencies.max,
        sampleRate: clip.audioFiles?.[0].sampleRate,
        labelsColor: "rgba(0,0,0,1)",
        labelsHzColor: "rgba(0,0,0,1)",
      });
      ws.registerPlugin(spectrogramPlugin);

      const regionsPlug = RegionsPlugin.create();
      ws.registerPlugin(regionsPlug);
      setRegionsPlugin(regionsPlug);

      ws.registerPlugin(TimelinePlugin.create());

      return () => {
        ws.destroy();
      };
    }
  }, [loaded, clip.audioFiles]);

  if (loaded) {
    return (
      <Box position="relative" width={1} height={1}>
        <AudioEditor
          readOnly={!canEditClip(user, clip)}
          clip={clip}
          wavesurfer={wavesurfer}
          regionsPlugin={regionsPlugin}
          mode={mode}
          channels={channels}
        >
          <div
            ref={minimapRef}
            sx={{
              backgroundColor: "#FFF",
              width: "100%",
              height: "100%",
            }}
          />
          <div ref={containerRef} />
        </AudioEditor>
        <ContentTitleBar
          menu={
            <ClipTitleBarMenu
              user={user}
              clip={clip}
              readOnly={!canEditClip(user, clip)}
              onChange={load}
              color="light"
              navigateOnDelete
            />
          }
          name={clip.description}
          label={`${clip.afCase.caseName}`}
          onBack={() => navigate(`/app/cases/${caseId}/clips`)}
        />
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer,
          }}
        >
          <HistoryDrawer caseId={caseId} clipId={clip.clipId} page="editor" />
        </Box>
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          width: 1,
          height: window.innerHeight,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
}
